import type { StoreApi, UseBoundStore } from 'zustand';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

export interface LightboxImage {
  src: string;
  alt: string;
  /**
   * True when the image was originally flagged NSFW and the viewer revealed
   * it before opening the lightbox. Drives the "Hide again" affordance —
   * non-NSFW images don't get the button.
   */
  wasNsfw?: boolean;
}

interface LightboxState {
  open: boolean;
  images: LightboxImage[];
  currentIndex: number;
  openLightbox: (images: LightboxImage[], startIndex?: number) => void;
  closeLightbox: () => void;
  goToNext: () => void;
  goToPrev: () => void;
  goToIndex: (index: number) => void;
}

export const useLightboxStore: UseBoundStore<StoreApi<LightboxState>> =
  createTrackedStore<LightboxState>('useLightboxStore')((set, get) => ({
    open: false,
    images: [],
    currentIndex: 0,
    openLightbox: (images: LightboxImage[], startIndex?: number): void => {
      set({ open: true, images, currentIndex: startIndex ?? 0 });
    },
    closeLightbox: (): void => {
      set({ open: false });
    },
    goToNext: (): void => {
      const { currentIndex, images } = get();
      if (images.length === 0) return;
      set({ currentIndex: (currentIndex + 1) % images.length });
    },
    goToPrev: (): void => {
      const { currentIndex, images } = get();
      if (images.length === 0) return;
      set({ currentIndex: (currentIndex - 1 + images.length) % images.length });
    },
    goToIndex: (index: number): void => {
      const { images } = get();
      if (index >= 0 && index < images.length) {
        set({ currentIndex: index });
      }
    },
  }));
